import { useEffect, useState } from 'react';
import { DatabaseControllerInterface } from '../../electron/src/types';

export function useLocalDatabase() {
  const [localDatabasePath, setLocalDatabasePath] = useState<string | null>(null);
  const [localDatabase, setLocalDatabase] = useState<DatabaseControllerInterface | undefined>(
    undefined
  );
  const localStorageItem = 'localDatabasePath';
  const databaseName = 'data.sqlite';

  // Effect to load path from localStorage or generate it
  useEffect(() => {
    const fromLocalStorage = localStorage.getItem(localStorageItem);
    if (fromLocalStorage) {
      setLocalDatabasePath(fromLocalStorage);
    } else {
      const fetchPath = async () => {
        try {
          let appDataPath;
          if (process.env.REACT_APP_LOCAL_FOLDER === 'true') {
            // get the current execution directory as path
            try {
              const path = (window as any).require('path');
              appDataPath = path.resolve('./');
            } catch (e) {
              appDataPath = typeof process.cwd === 'function' ? process.cwd() : './';
            }
          } else {
            appDataPath = await window.electron?.getPath('userData');
          }

          // Fallback to documents folder if userData is not available
          if (!appDataPath) {
            const docsPath = await window.electron?.getPath('documents');
            if (docsPath) {
              appDataPath = await window.electron?.joinPath(docsPath, 'djplayit');
              // Ensure the directory exists
              if (appDataPath) {
                await window.electron?.mkdir(appDataPath);
              }
            }
          }
          if (appDataPath) {
            // check if the folder exists and create it if not
            if (!(await window.electron?.isDirectory(appDataPath))) {
              await window.electron?.mkdir(appDataPath);
            }
            const file = await window.electron?.joinPath(appDataPath, databaseName);
            if (file) {
              localStorage.setItem(localStorageItem, file);
              setLocalDatabasePath(file);
            }
          } else {
            console.error('Could not determine a writable path for the database.');
          }
        } catch (error) {
          console.error('Failed to fetch database path:', error);
        }
      };
      fetchPath();
    }
  }, []);

  // Effect to create DB instance when path is available
  useEffect(() => {
    const initDB = async () => {
      if (localDatabasePath) {
        try {
          const db = await window.electron?.localDatabase(localDatabasePath);
          db?.createTables();
          setLocalDatabase(db);
        } catch (error) {
          console.error('Failed to initialize local database:', error);
        }
      }
    };
    initDB();
  }, [localDatabasePath]);

  return {
    localDatabasePath,
    setLocalDatabasePath,
    localDatabase,
    localDatabaseLoaded: localDatabase !== null,
  };
}
